import React, { useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getSnapshot } from '../utils/feedbackCapture';

const API_BASE = process.env.REACT_APP_API_URL;

const MAX_IMAGES = 4;
const MAX_IMAGE_BYTES = 6 * 1024 * 1024; // 6 MB

const fileToDataUrl = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const TYPE_OPTIONS = [
  { value: 'bug',         label: '🐛 Bug',              desc: 'Something is broken or behaving incorrectly' },
  { value: 'ux',          label: '✏️ UX Issue',          desc: 'Confusing or hard to use' },
  { value: 'feature',     label: '💡 Feature Request',   desc: 'I want something new' },
  { value: 'performance', label: '⚡ Performance',        desc: 'Slow or unresponsive' },
  { value: 'question',    label: '❓ Question',           desc: 'I need help understanding something' },
  { value: 'other',       label: '📝 Other',             desc: 'Something else' },
];

const PRIORITY_OPTIONS = [
  { value: 'low',      label: 'Low',      color: '#94a3b8' },
  { value: 'medium',   label: 'Medium',   color: '#60a5fa' },
  { value: 'high',     label: 'High',     color: '#f59e0b' },
  { value: 'critical', label: 'Critical', color: '#EC2124' },
];

function normalizeRole(user) {
  const r = user?.publicMetadata?.role || user?.publicMetadata?.roles;
  if (!r) return '';
  if (Array.isArray(r)) return (r[0] || '').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

export default function FeedbackButton() {
  const { user, isSignedIn } = useUser();
  const { getToken } = useAuth();
  const location = useLocation();

  const [open, setOpen]         = useState(false);
  const [step, setStep]         = useState('form'); // 'form' | 'submitting' | 'success' | 'error'

  const [type,        setType]        = useState('bug');
  const [title,       setTitle]       = useState('');
  const [description, setDescription] = useState('');
  const [priority,    setPriority]    = useState('medium');
  const [images,      setImages]      = useState([]); // array of base64 data URLs
  const [imgNote,     setImgNote]     = useState('');
  const fileInputRef = useRef(null);

  if (!isSignedIn) return null;

  const reset = () => {
    setStep('form');
    setType('bug');
    setTitle('');
    setDescription('');
    setPriority('medium');
    setImages([]);
    setImgNote('');
  };

  const addFiles = async fileList => {
    const list = Array.from(fileList || []).filter(f => f.type.startsWith('image/'));
    if (!list.length) return;
    let note = '';
    const accepted = [];
    for (const file of list) {
      if (file.size > MAX_IMAGE_BYTES) { note = 'Some images were skipped (over 6 MB).'; continue; }
      accepted.push(file);
    }
    const dataUrls = await Promise.all(accepted.map(fileToDataUrl));
    setImages(prev => {
      const room = MAX_IMAGES - prev.length;
      if (dataUrls.length > room) note = `You can attach up to ${MAX_IMAGES} images.`;
      return [...prev, ...dataUrls.slice(0, Math.max(0, room))];
    });
    setImgNote(note);
  };

  const handlePaste = e => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) { e.preventDefault(); addFiles(files); }
  };

  const removeImage = idx => setImages(prev => prev.filter((_, i) => i !== idx));

  const openModal = () => { reset(); setOpen(true); };
  const closeModal = () => { setOpen(false); };

  const submit = async () => {
    if (!title.trim()) return;
    setStep('submitting');
    try {
      const token = await getToken();
      const ctx = getSnapshot();
      const res = await fetch(`${API_BASE}/feedback`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type,
          title:          title.trim(),
          description:    description.trim(),
          priority,
          pageUrl:        location.pathname,
          pageName:       document.title,
          userAgent:      navigator.userAgent,
          userName:       user?.fullName || user?.firstName || '',
          userRole:       normalizeRole(user),
          apiCalls:       ctx.apiCalls,
          consoleErrors:  ctx.consoleErrors,
          screenWidth:    ctx.screenWidth,
          screenHeight:   ctx.screenHeight,
          sessionSeconds: ctx.sessionSeconds,
          images,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setStep('success');
      } else {
        setStep('error');
      }
    } catch {
      setStep('error');
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={openModal}
        title="Send feedback"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1500,
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #46BB89, #34d399)',
          border: 'none',
          boxShadow: '0 4px 16px rgba(70,187,137,0.4)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 22,
          transition: 'transform 0.15s, box-shadow 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 6px 24px rgba(70,187,137,0.55)';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(70,187,137,0.4)';
        }}
      >
        💬
      </button>

      {/* Modal */}
      {open && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed', inset: 0, zIndex: 2000,
            background: 'rgba(0,0,0,0.65)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            onPaste={handlePaste}
            style={{
              background: '#1a1d23',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 14,
              width: '100%',
              maxWidth: 520,
              boxShadow: '0 24px 64px rgba(0,0,0,0.6)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 20px 14px',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9' }}>Send Feedback</div>
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                  Page: <span style={{ color: '#94a3b8', fontFamily: 'monospace' }}>{location.pathname}</span>
                </div>
              </div>
              <button onClick={closeModal} style={{
                background: 'none', border: 'none', color: '#64748b',
                cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 2px',
              }}>×</button>
            </div>

            {/* Body */}
            <div style={{ padding: '18px 20px 20px' }}>
              {step === 'form' && (
                <>
                  {/* Type selector */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                      Type
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {TYPE_OPTIONS.map(t => (
                        <button
                          key={t.value}
                          onClick={() => setType(t.value)}
                          title={t.desc}
                          style={{
                            padding: '5px 12px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer',
                            border: type === t.value ? '1px solid #46BB89' : '1px solid rgba(255,255,255,0.12)',
                            background: type === t.value ? '#46BB8918' : 'transparent',
                            color: type === t.value ? '#46BB89' : '#94a3b8',
                            transition: 'all 0.12s',
                          }}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Title */}
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }}>
                      Title <span style={{ color: '#EC2124' }}>*</span>
                    </label>
                    <input
                      autoFocus
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      placeholder="Short summary of the issue…"
                      maxLength={140}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 7, color: '#f1f5f9',
                        padding: '8px 10px', fontSize: 13, outline: 'none',
                      }}
                    />
                  </div>

                  {/* Description */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 5 }}>
                      Description
                    </label>
                    <textarea
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Steps to reproduce, expected vs actual behaviour, or any extra context…"
                      rows={4}
                      style={{
                        width: '100%', boxSizing: 'border-box',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 7, color: '#f1f5f9',
                        padding: '8px 10px', fontSize: 13, outline: 'none',
                        resize: 'vertical', fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  {/* Screenshots */}
                  <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                      Screenshots
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      {images.map((src, i) => (
                        <div key={i} style={{ position: 'relative', width: 60, height: 60 }}>
                          <img
                            src={src}
                            alt={`attachment ${i + 1}`}
                            style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)' }}
                          />
                          <button
                            onClick={() => removeImage(i)}
                            title="Remove"
                            style={{
                              position: 'absolute', top: -6, right: -6, width: 18, height: 18,
                              borderRadius: '50%', border: 'none', background: '#EC2124', color: '#fff',
                              fontSize: 12, lineHeight: '18px', cursor: 'pointer', padding: 0,
                            }}
                          >×</button>
                        </div>
                      ))}
                      {images.length < MAX_IMAGES && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          style={{
                            width: 60, height: 60, borderRadius: 6,
                            border: '1px dashed rgba(255,255,255,0.2)', background: 'transparent',
                            color: '#94a3b8', cursor: 'pointer', fontSize: 20,
                          }}
                        >+</button>
                      )}
                    </div>
                    <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 5 }}>
                      Paste a screenshot (Ctrl/⌘+V) or click + to add — up to {MAX_IMAGES} images.
                    </div>
                    {imgNote && (
                      <div style={{ fontSize: 10.5, color: '#f59e0b', marginTop: 3 }}>{imgNote}</div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                      onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                    />
                  </div>

                  {/* Priority */}
                  <div style={{ marginBottom: 20 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                      Priority
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {PRIORITY_OPTIONS.map(p => (
                        <button
                          key={p.value}
                          onClick={() => setPriority(p.value)}
                          style={{
                            flex: 1, padding: '5px 0', borderRadius: 7, fontSize: 12,
                            fontWeight: 600, cursor: 'pointer',
                            border: priority === p.value ? `1px solid ${p.color}` : '1px solid rgba(255,255,255,0.12)',
                            background: priority === p.value ? p.color + '22' : 'transparent',
                            color: priority === p.value ? p.color : '#64748b',
                            transition: 'all 0.12s',
                          }}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Submit */}
                  <button
                    onClick={submit}
                    disabled={!title.trim()}
                    style={{
                      width: '100%', padding: '10px', borderRadius: 8,
                      background: title.trim() ? 'linear-gradient(135deg, #46BB89, #34d399)' : 'rgba(255,255,255,0.07)',
                      border: 'none',
                      color: title.trim() ? '#000' : '#64748b',
                      fontWeight: 700, fontSize: 14, cursor: title.trim() ? 'pointer' : 'not-allowed',
                      transition: 'all 0.15s',
                    }}
                  >
                    Submit Feedback
                  </button>
                </>
              )}

              {step === 'submitting' && (
                <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
                  <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
                  Sending feedback and creating GitHub issue…
                </div>
              )}

              {step === 'success' && (
                <div style={{ textAlign: 'center', padding: '28px 0' }}>
                  <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#f1f5f9', marginBottom: 6 }}>
                    Feedback received — thank you!
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>
                    Our team will review it shortly. A GitHub issue has been created automatically.
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    <button onClick={() => { reset(); }} style={{
                      padding: '7px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)',
                      border: '1px solid rgba(255,255,255,0.12)', color: '#94a3b8',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600,
                    }}>
                      Submit Another
                    </button>
                    <button onClick={closeModal} style={{
                      padding: '7px 18px', borderRadius: 7,
                      background: 'linear-gradient(135deg, #46BB89, #34d399)',
                      border: 'none', color: '#000',
                      cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    }}>
                      Done
                    </button>
                  </div>
                </div>
              )}

              {step === 'error' && (
                <div style={{ textAlign: 'center', padding: '28px 0' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>⚠️</div>
                  <div style={{ color: '#EC2124', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                    Submission failed
                  </div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>
                    Please try again or contact your administrator.
                  </div>
                  <button onClick={() => setStep('form')} style={{
                    padding: '7px 18px', borderRadius: 7,
                    background: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: '#94a3b8', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                  }}>
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
